import React, { useState, useEffect } from 'react';
import azureDevOpsService from './services/azureDevOpsService';

export const REPOSITORIES = [
  'platform-readyapi-argonos-adm',
  'platform-readyapi-argonos-dp',
  'platform-readyapi-argonos-de',
  'platform-readyapi-investigation',
  'argonos-robot'
];

export default function KpiPullRequests() {

  const TIME_RANGES = [
    { value: 7, label: 'Derniers 7 jours' },
    { value: 30, label: 'Derniers 30 jours' },
    { value: 90, label: 'Derniers 90 jours' },
    { value: 365, label: 'Dernière année' }
  ];

  const [selectedRange, setSelectedRange] = useState(30);
  const [prStats, setPrStats] = useState([]);
  const [totalPrs, setTotalPrs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [repoFilter, setRepoFilter] = useState('');

  useEffect(() => {
    const cached = sessionStorage.getItem('kpi-pr-range');
    if (cached) {
      setSelectedRange(parseInt(cached));
    }
    const cachedFilter = sessionStorage.getItem('kpi-pr-repo-filter');
    if (cachedFilter !== null) setRepoFilter(cachedFilter);
  }, []);

  useEffect(() => {
    if (selectedRange) {
      analyzePullRequests(selectedRange);
    }
  }, [selectedRange, repoFilter]);

  const analyzePullRequests = async (days) => {
    setLoading(true);
    setError(null);
    setPrStats([]);
    setTotalPrs(0);

    try {
      // Calculer la date de début
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - days);
      const fromDateStr = fromDate.toISOString().split('T')[0];

      // Récupérer tous les repos du projet
      const allRepos = await azureDevOpsService.getRepositories();
      
      // Filtrer pour ne garder que les repos qui nous intéressent
      let targetRepos = allRepos.filter(repo => 
        REPOSITORIES.some(name => repo.name.toLowerCase() === name.toLowerCase())
      );

      // Appliquer filtre par repository exact (si sélectionné)
      const f = (repoFilter || '').trim().toLowerCase();
      if (f) {
        targetRepos = targetRepos.filter(r => r.name.toLowerCase() === f);
      }

      if (targetRepos.length === 0) {
        setError('Aucun repository trouvé pour ce filtre');
        return;
      }

      // Agréger les PRs par auteur
      const authorMap = new Map();
      let total = 0;

      for (const repo of targetRepos) {
        try {
          const prs = await azureDevOpsService.getPullRequestsByRepository(
            repo.id,
            fromDateStr,
            'completed'
          );

          for (const pr of prs) {
            total++;
            const authorName = pr.createdBy?.displayName || 'Inconnu';
            const authorEmail = pr.createdBy?.uniqueName || '';
            
            if (!authorMap.has(authorName)) {
              authorMap.set(authorName, {
                name: authorName,
                email: authorEmail,
                count: 0,
                repos: new Set()
              });
            }
            
            const author = authorMap.get(authorName);
            author.count++;
            author.repos.add(repo.name);
          }
        } catch (err) {
          console.warn(`Erreur pour le repo ${repo.name}:`, err);
        }
      }

      // Convertir en tableau et trier par nombre de PRs
      const stats = Array.from(authorMap.values())
        .map(author => ({
          ...author,
          repos: Array.from(author.repos)
        }))
        .sort((a, b) => b.count - a.count);

      setPrStats(stats);
      setTotalPrs(total);
    } catch (e) {
      setError(e?.message || 'Erreur lors de l\'analyse des pull requests');
    } finally {
      setLoading(false);
    }
  };

  const handleRangeChange = (e) => {
    const days = parseInt(e.target.value);
    setSelectedRange(days);
    sessionStorage.setItem('kpi-pr-range', days);
  };

  const handleRepoFilterChange = (e) => {
    const value = e.target.value;
    setRepoFilter(value);
    sessionStorage.setItem('kpi-pr-repo-filter', value);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-bold mb-4">Pull Requests par Personne</h2>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Période d'analyse
            </label>
            <select
              value={selectedRange}
              onChange={handleRangeChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {TIME_RANGES.map(range => (
                <option key={range.value} value={range.value}>
                  {range.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Repository
            </label>
            <select
              value={repoFilter}
              onChange={handleRepoFilterChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Tous les repositories</option>
              {REPOSITORIES.map((name) => (
                <option key={name} value={name.toLowerCase()}>{name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Analyse des pull requests en cours...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Results */}
      {!loading && !error && prStats.length > 0 && (
        <div>
          {/* Summary */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <div>
                <div className="text-sm text-gray-600">Total PR completed</div>
                <div className="text-3xl font-bold text-blue-600">{totalPrs}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">Contributeurs</div>
                <div className="text-3xl font-bold text-gray-900">{prStats.length}</div>
              </div>
            </div>
          </div>

          {/* Stats Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contributeur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PR completed
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Repositories
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {prStats.map((author, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {idx + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{author.name}</div>
                      {author.email && (
                        <div className="text-xs text-gray-500">{author.email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        {author.count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      <div className="flex flex-wrap gap-1">
                        {author.repos.map((repo, i) => (
                          <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700">
                            {repo}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && prStats.length === 0 && (
        <div className="text-center py-6 text-gray-600">
          Aucune pull request trouvée pour la période sélectionnée.
        </div>
      )}
    </div>
  );
}
